// ECHOLOOP — entry point: game state machine, fixed-step loop, camera, HUD.

import { TILE, VIEW_W, VIEW_H, TICK } from './types';
import { LEVELS, parseLevel } from './levels';
import { Sim, PLAYER_W, PLAYER_H } from './sim';
import {
  initInput,
  input,
  isTouchDevice,
  touchKeyDown,
  touchKeyUp,
  touchTap,
  clearTouchHeld,
} from './input';
import { unlockAudio, sfx } from './audio';
import { Intro } from './intro';
import { Platform } from './platform';
import { mountPlatformChip } from './platform-chip';
import { maybeAutoOpenPanel } from './platform-panel';
import * as authClient from './auth';
import {
  isLevelUnlocked,
  chapterForLevel,
  unlockChapter,
  rewardLevelClear,
  getChapters,
  GAME_ID,
  type Chapter,
} from './progression';
import {
  drawBackdrop,
  drawWorld,
  drawPost,
  drawPlayerTrail,
  spawnBurst,
  updateParticles,
  pushTrail,
  ageTrail,
  type TrailPoint,
} from './render';

type GameState = 'intro' | 'menu' | 'select' | 'play' | 'won' | 'end' | 'mode3d' | 'locked';
// Which campaign the level selector / locked-wall flow targets. The 2D campaign
// runs inline; the 3D campaign hands the chosen index to the lazy game3d chunk.
type Campaign = '2d' | '3d';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
const stageEl = document.getElementById('stage') as HTMLElement;

let state: GameState = 'menu';
let intro: Intro | null = null;
let skipIntro = false; // set by a pointer tap during the intro

type Mode = 'third' | 'first' | '2d';
const MODES: Array<{ id: Mode; title: string; blurb: string; key: string }> = [
  { id: 'first', title: '3D · FIRST PERSON', blurb: 'full-3D parkour through your own eyes — tap V in-game for third-person', key: '1' },
  { id: '2d', title: 'CLASSIC 2D', blurb: 'the original side-on echo puzzler', key: '2' },
];
// Touch devices default to CLASSIC 2D (the cheaper, more touch-friendly mode);
// desktop keeps the 3D first-person mode highlighted by default.
const TOUCH = isTouchDevice();
let menuSel = TOUCH ? MODES.findIndex((m) => m.id === '2d') : 0;
// A level entry waiting behind the cinematic (fired on first entry of level 1).
interface PendingEntry { campaign: Campaign; camMode: 'third' | 'first'; idx: number }
let pendingEntry: PendingEntry | null = null;
// The selector / locked-wall flow tracks which campaign + camera it targets so a
// purchase resumes into the right place (2D inline, 3D into the lazy chunk).
let selectCampaign: Campaign = '2d';
let selectCamMode: 'third' | 'first' = 'third';
let levelIdx = 0;
let sim = new Sim(parseLevel(LEVELS[0]!));
let camX = 0;
let time = 0;
let flash = 0; // white flash alpha
let redFlash = 0;
let shake = 0; // screen-shake intensity, px
let rewindSweep = 0; // 1→0 timeline-rewind wipe fired when an echo is dropped
let cardT = 0; // level-card overlay timer
let wonT = 0;
let bestMsg = ''; // end-screen best-time line
let pendingLevel = 0; // level waiting behind a locked chapter gate
let lockedCampaign: Campaign = '2d'; // which campaign the gated level belongs to
let lockedChapter: Chapter | null = null;
let lockedFlash = ''; // transient feedback line in the unlock overlay
let lockedFlashT = 0;
let creditPop = 0; // brief glow when the balance changes

function loadBest(key: string): number | null {
  try {
    const v = localStorage.getItem(key);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

function saveBest(key: string, seconds: number): boolean {
  const prev = loadBest(key);
  if (prev !== null && prev <= seconds) return false;
  try {
    localStorage.setItem(key, seconds.toFixed(1));
  } catch {
    /* private mode etc. — non-essential */
  }
  return true;
}

const INTRO_SEEN_KEY = 'echoloop.introSeen.v1';
function introSeen(): boolean {
  try {
    return localStorage.getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}
function markIntroSeen(): void {
  try {
    localStorage.setItem(INTRO_SEEN_KEY, '1');
  } catch {
    /* private mode etc. — non-essential */
  }
}
function startIntro(): void {
  intro = new Intro();
  skipIntro = false;
  state = 'intro';
}

// Hand the chosen index into the lazy 3D chunk (three.js stays out of the 2D
// bundle). `start` is the 0-based level the selector picked.
function enter3D(camMode: 'third' | 'first', start: number): void {
  state = 'mode3d';
  const stage = document.getElementById('stage')!;
  stage.style.display = 'none';
  import('./three/game3d')
    .then(({ startGame3D }) => {
      startGame3D(() => {
        stage.style.display = '';
        menuBest2d = loadBest('echoloop.best2d');
        menuBest3d = loadBest('echoloop.best3d');
        state = 'menu';
      }, { camMode, startLevel: start });
    })
    .catch((err: unknown) => {
      // failed chunk load (stale dev server / offline) — don't strand the
      // player on a black screen; fall back to the menu
      console.error('[echoloop] 3D mode failed to load:', err);
      stage.style.display = '';
      state = 'menu';
    });
}

// The camera mode last picked from the menu (for the 3D campaign). The 2D card
// has no camera; the two 3D cards carry 'first'/'third'.
function camModeFor(mode: Mode): 'third' | 'first' {
  return mode === 'first' ? 'first' : 'third';
}

// Actually enter a level once it's known to be UNLOCKED. The cinematic now fires
// here — only the first time the player enters level 1 (idx 0) — gated by the
// introSeen flag (NOT on every mode pick or level select).
function enterLevel(campaign: Campaign, camMode: 'third' | 'first', idx: number): void {
  unlockAudio();
  if (idx === 0 && !introSeen()) {
    pendingEntry = { campaign, camMode, idx };
    startIntro();
    return;
  }
  if (campaign === '2d') {
    stats.length = 0;
    startLevel(idx);
  } else {
    enter3D(camMode, idx);
  }
}

// Pick the highlighted mode → open the level selector for its campaign. The
// intro no longer fires here; it fires on first entry of level 1 (see enterLevel).
function selectMode(): void {
  const mode = MODES[menuSel]!.id;
  unlockAudio();
  openSelect(mode === '2d' ? '2d' : '3d', camModeFor(mode));
}

let menuBest2d = loadBest('echoloop.best2d');
let menuBest3d = loadBest('echoloop.best3d');
const trail: TrailPoint[] = [];
const stats: Array<{ seconds: number; echoes: number }> = [];

// ---- DOM main menu (crisp native-res; replaces the old scaled canvas menu) ----
const menuRoot = document.createElement('div');
menuRoot.id = 'menu';
const menuStyle = document.createElement('style');
menuStyle.textContent = `
#menu{position:fixed;inset:0;z-index:6;display:none;color:#e7ecf6;
  background:radial-gradient(1100px 760px at 50% 34%, #160b29 0%, #0a0613 58%, #06040c 100%);
  font-family:'JetBrains Mono',ui-monospace,Menlo,monospace;}
#menu .wrap{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;gap:56px;
  padding:max(40px,env(safe-area-inset-top)) max(40px,env(safe-area-inset-right)) max(40px,env(safe-area-inset-bottom)) max(40px,env(safe-area-inset-left));}
#menu .keys{min-width:208px;}
#menu .keys h4{margin:0 0 14px;font-size:11px;letter-spacing:.34em;color:#56f3ff;font-weight:700;}
#menu .keys .row{display:flex;justify-content:space-between;gap:18px;padding:7px 0;font-size:12px;border-bottom:1px solid rgba(214,226,240,.07);}
#menu .keys .row span{color:rgba(214,226,240,.5);letter-spacing:.06em;}
#menu .keys .row b{color:#e7ecf6;font-weight:600;letter-spacing:.04em;}
#menu .center{display:flex;flex-direction:column;align-items:center;width:444px;}
#menu .title{font-size:46px;font-weight:800;letter-spacing:.34em;color:#f4f8ff;margin:0;text-indent:.34em;}
#menu .tag{font-size:12px;letter-spacing:.26em;color:#d44bff;margin:8px 0 16px;}
#menu .how{width:100%;margin:0 0 20px;display:flex;flex-direction:column;gap:9px;}
#menu .how .step{display:flex;align-items:flex-start;gap:11px;font-size:12px;line-height:1.5;
  color:rgba(214,226,240,.66);letter-spacing:.015em;text-align:left;}
#menu .how .step b{flex:none;width:19px;height:19px;border-radius:50%;display:grid;place-items:center;
  font-size:11px;font-weight:800;color:#0a0613;background:#56f3ff;margin-top:1px;}
#menu .how .step:nth-child(2) b{background:#d44bff;}
#menu .how .step:nth-child(3) b{background:#5dff9c;}
#menu .how .step em{color:#f4f8ff;font-style:normal;font-weight:700;}
#menu .cards{display:flex;flex-direction:column;gap:12px;width:100%;}
#menu .card{display:flex;align-items:center;gap:16px;width:100%;cursor:pointer;border-radius:12px;padding:14px 18px;
  background:rgba(214,226,240,.035);border:1px solid rgba(214,226,240,.12);transition:border-color .12s,background .12s;}
#menu .card:hover{border-color:rgba(86,243,255,.5);}
#menu .card.sel{border-color:#56f3ff;background:rgba(86,243,255,.1);box-shadow:0 0 22px rgba(86,243,255,.18);}
#menu .card .num{font-size:13px;color:rgba(214,226,240,.4);font-weight:700;width:16px;}
#menu .card.sel .num{color:#56f3ff;}
#menu .card .body{flex:1;}
#menu .card .name{font-size:19px;font-weight:800;letter-spacing:.12em;color:rgba(214,226,240,.58);}
#menu .card.sel .name{color:#f4f8ff;}
#menu .card .blurb{font-size:11.5px;letter-spacing:.02em;color:rgba(214,226,240,.34);margin-top:3px;}
#menu .card.sel .blurb{color:rgba(214,226,240,.72);}
#menu .card .def{font-size:9px;letter-spacing:.2em;color:rgba(255,217,102,.85);font-weight:700;}
#menu .prompt{margin-top:22px;font-size:12px;letter-spacing:.1em;color:#56f3ff;font-weight:700;}
#menu .meta{margin-top:10px;font-size:10px;letter-spacing:.14em;color:rgba(214,226,240,.4);text-align:center;line-height:1.8;}
/* Portrait / narrow phones + small tablets: stack vertically, hide the keyboard
   CONTROLS reference (touch players don't need it), make the centre column fluid
   so it never overflows 375–430px-wide screens, and grow tap targets. */
@media (max-width: 720px), (orientation: portrait) and (max-width: 900px){
  #menu .wrap{flex-direction:column;align-items:center;justify-content:flex-start;gap:18px;overflow-y:auto;}
  #menu .keys{display:none;}
  #menu .center{width:100%;max-width:444px;}
  #menu .title{font-size:9vw;}
  #menu .card{padding:18px;}
  #menu .card .name{font-size:16px;}
  #menu .prompt{margin-top:16px;}
}
`;
document.head.appendChild(menuStyle);
menuRoot.innerHTML = `
  <div class="wrap">
    <div class="keys">
      <h4>CONTROLS</h4>
      <div class="row"><span>MOVE</span><b>W A S D</b></div>
      <div class="row"><span>JUMP</span><b>SPACE</b></div>
      <div class="row"><span>DROP ECHO</span><b>R</b></div>
      <div class="row"><span>THROW · 3D</span><b>F / CLICK</b></div>
      <div class="row"><span>VIEW · 3D</span><b>V</b></div>
      <div class="row"><span>WIPE LEVEL</span><b>X</b></div>
      <div class="row"><span>REPLAY INTRO</span><b>I</b></div>
    </div>
    <div class="center">
      <h1 class="title">ECHOLOOP</h1>
      <div class="tag">your past selves are the level</div>
      <div class="how">
        <div class="step"><b>1</b><span>RUN for the exit portal — but a wall or sealed gate blocks the way.</span></div>
        <div class="step"><b>2</b><span>Press <em>R</em> to drop an ECHO: a replay of your run that re-walks it and freezes solid — a platform to stand on, a weight to hold buttons.</span></div>
        <div class="step"><b>3</b><span>Climb your past selves and thread the open gates to reach the EXIT.</span></div>
      </div>
      <div class="cards"></div>
      <div class="prompt">W / S or mouse to choose &nbsp;·&nbsp; ENTER or click to play</div>
      <div class="meta"></div>
    </div>
  </div>`;
document.body.appendChild(menuRoot);

const menuCardsBox = menuRoot.querySelector('.cards') as HTMLElement;
const menuMetaEl = menuRoot.querySelector('.meta') as HTMLElement;
const menuCardEls: HTMLDivElement[] = MODES.map((m, i) => {
  const el = document.createElement('div');
  el.className = 'card';
  el.innerHTML =
    `<div class="num">${m.key}</div>` +
    `<div class="body"><div class="name">${m.title}</div><div class="blurb">${m.blurb}</div></div>` +
    (i === menuSel ? '<div class="def">DEFAULT</div>' : '');
  el.addEventListener('mouseenter', () => {
    menuSel = i;
    syncMenu();
  });
  el.addEventListener('click', () => {
    menuSel = i;
    selectMode();
  });
  menuCardsBox.appendChild(el);
  return el;
});
let prevMenuShown = false;

function syncMenu(): void {
  for (let i = 0; i < menuCardEls.length; i++) menuCardEls[i]!.classList.toggle('sel', i === menuSel);
  const parts: string[] = [];
  if (menuBest3d !== null) parts.push(`3D BEST ${menuBest3d.toFixed(1)}s`);
  if (menuBest2d !== null) parts.push(`2D BEST ${menuBest2d.toFixed(1)}s`);
  const prog = Platform.getProgress<{ furthest: number }>(GAME_ID, { furthest: 0 });
  const cred =
    prog.furthest > 0
      ? `${Platform.balance()} CREDITS &nbsp;·&nbsp; FURTHEST ${prog.furthest}/${LEVELS.length}`
      : `${Platform.balance()} CREDITS`;
  menuMetaEl.innerHTML = (parts.length ? parts.join(' &nbsp;·&nbsp; ') + '<br/>' : '') + cred;
}

// ---- LEVEL SELECTOR (DOM overlay, sibling to the menu) ----------------------
// Shown after the mode pick. A grid of level tiles: each reads isLevelUnlocked
// (progression) for lock state + the shared `furthest` progress for "reached".
// Clicking an UNLOCKED tile starts that level (2D inline / 3D lazy chunk);
// clicking a LOCKED tile routes into the unlock wall (the `locked` state). The
// SAME selector drives both campaigns — the chosen index is handed to
// startLevel (2D) or startGame3D (3D).

// The 3D campaign has 35 levels (LEVELS3D). Kept as a constant so the selector +
// locked-wall copy never need to static-import the lazy 3D chunk. The 3D level
// NAMES are fetched lazily (pure-data module, no three.js) on first 3D select.
const LEVELS3D_COUNT = 35;
let levels3dNames: string[] | null = null;

const selectRoot = document.createElement('div');
selectRoot.id = 'select';
const selectStyle = document.createElement('style');
selectStyle.textContent = `
#select{position:fixed;inset:0;z-index:6;display:none;color:#e7ecf6;
  background:radial-gradient(1100px 760px at 50% 30%, #160b29 0%, #0a0613 58%, #06040c 100%);
  font-family:'JetBrains Mono',ui-monospace,Menlo,monospace;}
#select .swrap{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;
  padding:max(26px,env(safe-area-inset-top)) max(24px,env(safe-area-inset-right)) max(24px,env(safe-area-inset-bottom)) max(24px,env(safe-area-inset-left));
  overflow-y:auto;}
#select .shead{display:flex;align-items:baseline;gap:16px;margin:6px 0 4px;}
#select .stitle{font-size:24px;font-weight:800;letter-spacing:.22em;color:#f4f8ff;margin:0;}
#select .smode{font-size:11px;letter-spacing:.24em;color:#d44bff;font-weight:700;}
#select .ssub{font-size:11px;letter-spacing:.12em;color:rgba(214,226,240,.5);margin:0 0 16px;text-align:center;line-height:1.7;}
#select .ssub b{color:#ffe08a;font-weight:700;}
#select .grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(108px,1fr));gap:11px;
  width:100%;max-width:760px;}
#select .tile{position:relative;cursor:pointer;border-radius:11px;padding:12px 12px 13px;min-height:66px;
  background:rgba(214,226,240,.04);border:1px solid rgba(214,226,240,.12);
  transition:border-color .12s,background .12s,transform .06s;display:flex;flex-direction:column;gap:5px;
  -webkit-tap-highlight-color:transparent;}
#select .tile:hover{border-color:rgba(86,243,255,.5);background:rgba(86,243,255,.06);}
#select .tile:active{transform:scale(.97);}
#select .tile .tn{font-size:12px;font-weight:800;letter-spacing:.08em;color:#56f3ff;}
#select .tile .tname{font-size:10.5px;letter-spacing:.02em;color:rgba(214,226,240,.7);line-height:1.3;
  overflow:hidden;text-overflow:ellipsis;}
#select .tile .tnext{position:absolute;top:7px;right:9px;font-size:8px;letter-spacing:.18em;color:#5dff9c;font-weight:700;}
/* locked tiles: dimmed, amber lock chip, click → unlock wall */
#select .tile.locked{border-color:rgba(255,217,102,.28);background:rgba(255,217,102,.04);}
#select .tile.locked .tn{color:rgba(255,217,102,.85);}
#select .tile.locked .tname{color:rgba(214,226,240,.4);}
#select .tile.locked:hover{border-color:rgba(255,217,102,.6);background:rgba(255,217,102,.08);}
#select .tile .tlock{position:absolute;top:7px;right:9px;font-size:12px;opacity:.8;}
#select .tile .tprice{font-size:9px;letter-spacing:.06em;color:rgba(255,217,102,.8);font-weight:700;}
#select .sfoot{margin-top:18px;display:flex;gap:12px;align-items:center;flex-wrap:wrap;justify-content:center;}
#select .sbtn{cursor:pointer;font-family:inherit;font-weight:800;letter-spacing:.1em;font-size:12px;color:#e7ecf6;
  background:rgba(20,12,38,.5);border:1px solid rgba(214,226,240,.22);border-radius:11px;padding:11px 22px;
  transition:border-color .12s,background .12s;-webkit-tap-highlight-color:transparent;}
#select .sbtn:hover{border-color:#56f3ff;}
#select .scred{font-size:11px;letter-spacing:.1em;color:rgba(255,217,102,.85);font-weight:700;}
@media (max-width:560px){
  #select .grid{grid-template-columns:repeat(auto-fill,minmax(92px,1fr));gap:9px;}
  #select .stitle{font-size:20px;}
}
`;
document.head.appendChild(selectStyle);
selectRoot.innerHTML = `
  <div class="swrap">
    <div class="shead"><h2 class="stitle">SELECT LEVEL</h2><span class="smode"></span></div>
    <div class="ssub"></div>
    <div class="grid"></div>
    <div class="sfoot">
      <button class="sbtn back">◄ BACK</button>
      <span class="scred"></span>
    </div>
  </div>`;
document.body.appendChild(selectRoot);
const selModeEl = selectRoot.querySelector('.smode') as HTMLElement;
const selSubEl = selectRoot.querySelector('.ssub') as HTMLElement;
const selGridEl = selectRoot.querySelector('.grid') as HTMLElement;
const selCredEl = selectRoot.querySelector('.scred') as HTMLElement;
(selectRoot.querySelector('.back') as HTMLButtonElement).addEventListener('click', () => {
  sfx.button();
  closeSelect();
});

// Build the tile grid for the active campaign. Re-render after a balance change
// or a returning purchase so lock states + the credit line stay live.
function buildSelectGrid(): void {
  const is3d = selectCampaign === '3d';
  const count = is3d ? LEVELS3D_COUNT : LEVELS.length;
  const names = is3d ? levels3dNames : LEVELS.map((l) => l.name);
  const prog = Platform.getProgress<{ furthest: number }>(GAME_ID, { furthest: 0 });
  const furthest = prog.furthest; // 1-based count of reached levels
  selModeEl.textContent = is3d ? '3D CAMPAIGN' : 'CLASSIC 2D';
  const freeCount = chapterForLevel(0) === null ? firstGatedIdx() : 0;
  selSubEl.innerHTML =
    `the first <b>${freeCount}</b> levels are free · clear new levels to earn credits · ` +
    'unlock later chapters with credits — or buy now';
  selCredEl.textContent = `${Platform.balance()} CREDITS`;
  selGridEl.innerHTML = '';
  for (let i = 0; i < count; i++) {
    const unlocked = isLevelUnlocked(i);
    const ch = chapterForLevel(i);
    const tile = document.createElement('div');
    tile.className = `tile${unlocked ? '' : ' locked'}`;
    const nm = names && names[i] ? names[i]! : `LEVEL ${String(i + 1).padStart(2, '0')}`;
    const reached = i + 1 <= furthest; // already reached this level
    tile.innerHTML =
      `<div class="tn">${String(i + 1).padStart(2, '0')}</div>` +
      `<div class="tname">${nm}</div>` +
      (unlocked
        ? reached && i > 0
          ? '<div class="tnext">DONE</div>'
          : ''
        : `<div class="tprice">${ch ? ch.price : 0} CR</div>`);
    tile.addEventListener('click', () => {
      if (unlocked) {
        enterLevel(selectCampaign, selectCamMode, i);
      } else {
        sfx.button();
        gateLevel(selectCampaign, i);
      }
    });
    selGridEl.appendChild(tile);
  }
}

// The first level index that is gated (== freeLevels). Derived from progression
// so it tracks the live config / fallback.
function firstGatedIdx(): number {
  let i = 0;
  while (i < 200 && chapterForLevel(i) === null) i++;
  return i;
}

function openSelect(campaign: Campaign, camMode: 'third' | 'first'): void {
  selectCampaign = campaign;
  selectCamMode = camMode;
  state = 'select';
  if (campaign === '3d' && !levels3dNames) {
    // Lazily pull the pure-data 3D level list for nicer tile labels — this is
    // the levels3d module (no three.js), so it never drags in the 3D chunk.
    import('./three/levels3d')
      .then(({ LEVELS3D }) => {
        levels3dNames = LEVELS3D.map((l) => l.name);
        if (state === 'select' && selectCampaign === '3d') buildSelectGrid();
      })
      .catch(() => {
        /* names are cosmetic — generic "LEVEL NN" labels stand if it fails */
      });
  }
  buildSelectGrid();
}

function closeSelect(): void {
  state = 'menu';
}

let prevPressedCount = 0;
// Echoes that have already played their "lock into place" puff this level, so
// the freeze feedback fires once per past self (not every replay loop).
const frozenEchoIds = new Set<number>();
// Jump press latched across render frames until a sim tick consumes it.
// The render loop can run faster than the 60Hz sim (120Hz+ displays), so a
// frame-scoped edge would be cleared on zero-tick frames and the jump lost.
let pendingJump = false;

// ---- 2D touch controls -------------------------------------------------------
// On-screen controls mounted ONLY on touch devices (desktop is untouched).
// Every button drives the SAME virtual keys the keyboard feeds via input.ts:
//   ◄ / ►       → touchKeyDown/Up('arrowleft' / 'arrowright')  -> input.left()/right()
//   JUMP        → touchKeyDown/Up(' ')                          -> input.jumpHeld()/jumpPressed()
//   ECHO (R)    → touchTap('r')                                 -> input.echoPressed()
//   WIPE (X)    → touchTap('x')                                 -> input.resetPressed()
//   CONTINUE    → touchTap('enter')                             -> input.confirmPressed()  (won/end)
//   UNLOCK [U]  → touchTap('u')                                 -> input.unlockPressed()   (locked)
//   BACK [ESC]  → touchTap('escape')                            -> input.backPressed()     (locked)
// so the sim/state-machine reacts to touch exactly as it does to a keyboard.
// (Ads are disabled — Platform.adsEnabled() === false — so no [V] button.)
const touch2d = (() => {
  if (!TOUCH) {
    return { sync: (_: GameState) => {} };
  }

  const root = document.createElement('div');
  root.id = 'touch2d';
  const style = document.createElement('style');
  style.textContent = `
#touch2d{position:fixed;inset:0;z-index:8;display:none;pointer-events:none;
  font-family:'JetBrains Mono',ui-monospace,Menlo,monospace;
  -webkit-user-select:none;user-select:none;touch-action:none;}
#touch2d .grp{position:absolute;display:flex;pointer-events:none;}
#touch2d button{pointer-events:auto;touch-action:none;-webkit-tap-highlight-color:transparent;
  font-family:inherit;font-weight:800;letter-spacing:.08em;color:#e7ecf6;cursor:pointer;
  background:rgba(20,12,38,.5);border:1.5px solid rgba(214,226,240,.22);border-radius:16px;
  display:grid;place-items:center;transition:background .08s,border-color .08s,transform .06s;}
#touch2d button:active{background:rgba(86,243,255,.22);border-color:#56f3ff;transform:scale(.94);}
/* movement: bottom-left twin pad */
#touch2d .move{left:max(16px,env(safe-area-inset-left));bottom:max(20px,env(safe-area-inset-bottom));gap:14px;}
#touch2d .move button{width:72px;height:72px;font-size:30px;border-radius:50%;}
/* actions: bottom-right — JUMP large, ECHO/WIPE stacked beside it */
#touch2d .act{right:max(16px,env(safe-area-inset-right));bottom:max(20px,env(safe-area-inset-bottom));
  align-items:flex-end;gap:14px;}
#touch2d .act .stack{display:flex;flex-direction:column;gap:12px;}
#touch2d .act .jump{width:96px;height:96px;border-radius:50%;font-size:15px;
  border-color:rgba(93,255,156,.5);}
#touch2d .act .jump:active{background:rgba(93,255,156,.24);border-color:#5dff9c;}
#touch2d .act .echo{width:74px;height:54px;font-size:13px;border-color:rgba(212,75,255,.55);}
#touch2d .act .echo:active{background:rgba(212,75,255,.26);border-color:#d44bff;}
#touch2d .act .wipe{width:74px;height:54px;font-size:13px;}
/* full-screen tap layer for won/end (tap anywhere to continue) + a visible chip */
#touch2d .tapnext{position:absolute;inset:0;pointer-events:auto;background:transparent;
  border:none;border-radius:0;display:block;}
#touch2d .ovbtns{left:50%;bottom:max(40px,env(safe-area-inset-bottom));transform:translateX(-50%);
  gap:14px;align-items:center;}
#touch2d .ovbtns button{min-width:120px;min-height:52px;padding:0 22px;font-size:14px;}
#touch2d .ovbtns .primary{border-color:#56f3ff;color:#56f3ff;}
@media (max-width:560px){
  #touch2d .move button{width:62px;height:62px;font-size:26px;}
  #touch2d .act .jump{width:84px;height:84px;}
}
`;
  document.head.appendChild(style);

  const grp = (cls: string): HTMLDivElement => {
    const d = document.createElement('div');
    d.className = `grp ${cls}`;
    root.appendChild(d);
    return d;
  };

  // held-key button: latches the virtual key down while pressed (pointer/touch).
  const holdBtn = (label: string, cls: string, key: string): HTMLButtonElement => {
    const b = document.createElement('button');
    b.className = cls;
    b.textContent = label;
    const down = (e: Event): void => {
      e.preventDefault();
      touchKeyDown(key);
    };
    const up = (e: Event): void => {
      e.preventDefault();
      touchKeyUp(key);
    };
    b.addEventListener('pointerdown', down);
    b.addEventListener('pointerup', up);
    b.addEventListener('pointercancel', up);
    b.addEventListener('pointerleave', up);
    return b;
  };

  // tap button: fires a single press edge for the virtual key.
  const tapBtn = (label: string, cls: string, key: string): HTMLButtonElement => {
    const b = document.createElement('button');
    b.className = cls;
    b.textContent = label;
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      touchTap(key);
    });
    return b;
  };

  // movement pad
  const moveG = grp('move');
  moveG.appendChild(holdBtn('◄', 'left', 'arrowleft'));
  moveG.appendChild(holdBtn('►', 'right', 'arrowright'));

  // action cluster: ECHO/WIPE stacked + JUMP
  const actG = grp('act');
  const stack = document.createElement('div');
  stack.className = 'stack';
  stack.appendChild(tapBtn('ECHO', 'echo', 'r'));
  stack.appendChild(tapBtn('WIPE', 'wipe', 'x'));
  actG.appendChild(stack);
  actG.appendChild(holdBtn('JUMP', 'jump', ' '));

  // won/end: a full-screen tap layer + a labelled CONTINUE chip
  const tapNext = document.createElement('button');
  tapNext.className = 'tapnext';
  tapNext.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    touchTap('enter');
  });
  root.appendChild(tapNext);
  const nextG = grp('ovbtns');
  nextG.appendChild(tapBtn('CONTINUE', 'primary', 'enter'));

  // locked overlay: UNLOCK + BACK
  const lockG = grp('ovbtns');
  lockG.appendChild(tapBtn('UNLOCK', 'primary', 'u'));
  lockG.appendChild(tapBtn('BACK', 'back', 'escape'));

  document.body.appendChild(root);

  const showGroup = (el: HTMLElement, on: boolean): void => {
    el.style.display = on ? 'flex' : 'none';
  };

  return {
    sync(s: GameState): void {
      const onScreen = s === 'play' || s === 'won' || s === 'end' || s === 'locked';
      root.style.display = onScreen ? 'block' : 'none';
      const playing = s === 'play';
      showGroup(moveG, playing);
      showGroup(actG, playing);
      // won/end: tap-anywhere layer + CONTINUE chip
      const ended = s === 'won' || s === 'end';
      tapNext.style.display = ended ? 'block' : 'none';
      showGroup(nextG, ended);
      // locked: unlock / back chips
      showGroup(lockG, s === 'locked');
      if (!playing) clearTouchHeld(); // never leave a move key stuck when leaving play
    },
  };
})();

// ---- canvas sizing -----------------------------------------------------------

// HUD up-scale for small / portrait screens: the canvas is letterboxed to fit,
// so on a portrait phone (~390px wide) one VIEW_W unit is ~0.4 CSS px and the
// 12px HUD text renders ~5 device px. We multiply HUD font sizes by `hudScale`
// so the corner readouts / tutorial stay legible. World rendering is untouched
// (this only affects the canvas HUD/overlays via the `text()` helper). Bounded
// so desktop stays exactly 1.0.
let hudScale = 1;

function resize(): void {
  const scale = Math.min(window.innerWidth / VIEW_W, window.innerHeight / VIEW_H);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.style.width = `${Math.floor(VIEW_W * scale)}px`;
  canvas.style.height = `${Math.floor(VIEW_H * scale)}px`;
  canvas.width = Math.floor(VIEW_W * dpr);
  canvas.height = Math.floor(VIEW_H * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  // CSS px per VIEW unit (the displayed canvas size / its logical size).
  const cssPerUnit = Math.floor(VIEW_W * scale) / VIEW_W;
  // Want a ~12px logical HUD glyph to be at least ~15 CSS px tall on screen.
  const want = 15 / 12;
  hudScale = Math.max(1, Math.min(2.4, want / Math.max(0.001, cssPerUnit)));
}
window.addEventListener('resize', resize);
window.addEventListener('orientationchange', resize);
resize();

// ---- level / state transitions -------------------------------------------------

function startLevel(i: number): void {
  levelIdx = i;
  // remember how far the player has reached (shared progress store)
  const prog = Platform.getProgress<{ furthest: number }>(GAME_ID, { furthest: 0 });
  if (i + 1 > prog.furthest) Platform.saveProgress(GAME_ID, { furthest: i + 1 });
  sim = new Sim(parseLevel(LEVELS[i]!));
  camX = clampCam(sim.px - VIEW_W / 2);
  cardT = 3.2;
  trail.length = 0;
  prevPressedCount = 0;
  frozenEchoIds.clear();
  pendingJump = false;
  state = 'play';
}

function clampCam(x: number): number {
  const maxX = Math.max(0, sim.level.cols * TILE - VIEW_W);
  return Math.max(0, Math.min(maxX, x));
}

// Advance to 2D level `i`, or raise the credit-gate overlay if its chapter is
// locked. Used by the in-game "next loop" flow (2D campaign).
function advanceTo(i: number): void {
  if (isLevelUnlocked(i)) {
    startLevel(i);
    return;
  }
  gateLevel('2d', i);
}

// Raise the in-game unlock wall (the `locked` state) for `idx` in `campaign`.
// On a successful purchase the locked-state handler resumes into the right
// campaign (2D inline / 3D lazy chunk).
function gateLevel(campaign: Campaign, idx: number): void {
  lockedCampaign = campaign;
  pendingLevel = idx;
  lockedChapter = chapterForLevel(idx);
  lockedFlash = '';
  lockedFlashT = 0;
  state = 'locked';
}

// 1-based label of the last level a chapter gates (for the overlay copy). The
// campaign length differs (2D 30, 3D 35), so the final chapter's end depends on
// which campaign the locked wall is gating.
function chapterEndLabel(ch: Chapter, campaign: Campaign = lockedCampaign): number {
  const next = getChapters().find((c) => c.from > ch.from);
  return next ? next.from : campaign === '3d' ? LEVELS3D_COUNT : LEVELS.length;
}

// ---- fx pump -------------------------------------------------------------------

function pumpEvents(): void {
  for (const ev of sim.events) {
    switch (ev.kind) {
      case 'echo':
        spawnBurst(ev.x, ev.y, '#d44bff', 26, 240, 300);
        spawnBurst(ev.x, ev.y, '#ffffff', 10, 160, 200);
        flash = 0.35;
        shake = Math.max(shake, 3);
        rewindSweep = 1; // sweep the timeline back to t=0
        sfx.echo();
        break;
      case 'death':
        spawnBurst(ev.x, ev.y, '#ff4660', 30, 280, 500);
        redFlash = 0.4;
        shake = Math.max(shake, 7);
        sfx.death();
        break;
      case 'door':
        spawnBurst(ev.x, ev.y, '#9be8ff', 14, 150, 100);
        sfx.door();
        break;
      case 'land':
        spawnBurst(ev.x, ev.y, '#7a6fa8', 6, 70, 300);
        shake = Math.max(shake, 2);
        sfx.land();
        break;
      case 'win':
        spawnBurst(ev.x, ev.y, sim.level.def.accent, 50, 320, 80);
        spawnBurst(ev.x, ev.y, '#ffffff', 20, 200, 60);
        flash = 0.5;
        shake = Math.max(shake, 4);
        sfx.win();
        break;
      case 'deny':
        redFlash = 0.18;
        sfx.deny();
        break;
      case 'spring':
        spawnBurst(ev.x, ev.y, '#5af0b4', 18, 260, 200);
        spawnBurst(ev.x, ev.y, '#ffffff', 6, 150, 160);
        shake = Math.max(shake, 3);
        sfx.jump();
        break;
      case 'lever':
        spawnBurst(ev.x, ev.y, '#5dff9c', 12, 150, 120);
        sfx.button();
        break;
      case 'crumble':
        spawnBurst(ev.x, ev.y, '#ff9a4b', 20, 220, 440);
        shake = Math.max(shake, 4);
        sfx.land();
        break;
    }
  }
  sim.events.length = 0;
}

// ---- update ---------------------------------------------------------------------

let acc = 0;
let last = performance.now();

function frame(now: number): void {
  requestAnimationFrame(frame);
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  time += dt;
  acc += dt;

  // Show the crisp DOM menu only in the menu state; the level selector in the
  // select state; the canvas stage otherwise (the 3D mode owns its own screen).
  const showMenu = state === 'menu';
  const showSelect = state === 'select';
  menuRoot.style.display = showMenu ? 'block' : 'none';
  selectRoot.style.display = showSelect ? 'block' : 'none';
  stageEl.style.display = showMenu || showSelect || state === 'mode3d' ? 'none' : '';
  if (showMenu && !prevMenuShown) syncMenu();
  prevMenuShown = showMenu;
  // Mount/show the on-screen touch controls for the current state (no-op on
  // desktop). Synced before transitions so play→won lag is at most one frame.
  touch2d.sync(state);

  // ---- input / state transitions
  if (state === 'intro') {
    // any key or tap skips straight to the menu
    if (intro && (input.anyPressed() || skipIntro)) intro.skip();
    skipIntro = false;
    intro?.update(dt);
    if (!intro || intro.done) {
      markIntroSeen();
      intro = null;
      acc = 0;
      if (pendingEntry) {
        // resume the level-1 entry that triggered the cinematic
        const e = pendingEntry;
        pendingEntry = null;
        if (e.campaign === '2d') {
          stats.length = 0;
          startLevel(e.idx);
        } else {
          enter3D(e.camMode, e.idx);
        }
      } else {
        state = 'menu';
      }
    }
    render();
    input.endFrame();
    return;
  }
  if (state === 'mode3d') {
    // the 3D mode owns the screen and its own loop; idle here
    input.endFrame();
    return;
  }
  if (state === 'menu') {
    if (input.menuUpPressed()) {
      menuSel = (menuSel + MODES.length - 1) % MODES.length;
      sfx.button();
      syncMenu();
    } else if (input.menuDownPressed()) {
      menuSel = (menuSel + 1) % MODES.length;
      sfx.button();
      syncMenu();
    }
    const dk = ['1', '2', '3'].findIndex((d) => input.digitPressed(d));
    if (dk >= 0 && dk < MODES.length) {
      menuSel = dk;
      selectMode();
    } else if (input.confirmPressed()) {
      selectMode();
    } else if (input.replayIntroPressed()) {
      pendingEntry = null;
      startIntro();
    }
  } else if (state === 'select') {
    // ESC / BACK returns to the mode menu; I still replays the intro.
    if (input.backPressed()) {
      sfx.button();
      closeSelect();
    } else if (input.replayIntroPressed()) {
      pendingEntry = null;
      startIntro();
    }
  } else if (state === 'play') {
    if (input.echoPressed()) {
      unlockAudio();
      sim.spawnEcho();
      pumpEvents();
    }
    if (input.resetPressed()) {
      sim.resetLevel();
      trail.length = 0;
      frozenEchoIds.clear();
    }
  } else if (state === 'won') {
    wonT += dt;
    if (wonT > 0.7 && input.confirmPressed()) {
      if (levelIdx + 1 < LEVELS.length) advanceTo(levelIdx + 1);
      else {
        const total = stats.reduce((a, s) => a + (s ? s.seconds : 0), 0);
        const isNew = saveBest('echoloop.best2d', total);
        menuBest2d = loadBest('echoloop.best2d');
        bestMsg = isNew ? 'NEW BEST RUN!' : menuBest2d !== null ? `BEST RUN ${menuBest2d.toFixed(1)}s` : '';
        state = 'end';
      }
    }
  } else if (state === 'locked') {
    if (input.unlockPressed() && lockedChapter) {
      const res = unlockChapter(lockedChapter);
      if (res === 'purchased' || res === 'owned') {
        sfx.win();
        flash = 0.4;
        // Resume into the right campaign: 2D inline, 3D into the lazy chunk.
        if (lockedCampaign === '2d') {
          stats.length = 0;
          startLevel(pendingLevel);
        } else {
          enter3D(selectCamMode, pendingLevel);
        }
      } else {
        sfx.deny();
        redFlash = 0.18;
        const short = lockedChapter.price - Platform.balance();
        lockedFlash = `NOT ENOUGH CREDITS — ${short} SHORT. KEEP PLAYING TO EARN.`;
        lockedFlashT = 2.6;
      }
    } else if (Platform.adsEnabled() && input.adPressed()) {
      const got = Platform.watchAdReward();
      if (got > 0) {
        sfx.button();
        lockedFlash = `+${got} CREDITS`;
      } else {
        sfx.deny();
        lockedFlash = 'AD LIMIT REACHED TODAY';
      }
      lockedFlashT = 2.6;
    } else if (input.backPressed()) {
      // back to the level selector for this campaign so they can pick another.
      openSelect(lockedCampaign, selectCamMode);
    }
  } else if (state === 'end') {
    if (input.confirmPressed()) {
      state = 'menu';
    }
  }

  // ---- fixed-step sim
  if (state === 'play') {
    if (input.jumpPressed()) pendingJump = true;
    while (acc >= TICK) {
      acc -= TICK;
      const jumpQueued = pendingJump;
      pendingJump = false;
      const wasGrounded = sim.grounded;
      sim.tick({
        left: input.left(),
        right: input.right(),
        jumpPressed: jumpQueued,
        jumpHeld: input.jumpHeld(),
      });
      if (jumpQueued && !sim.grounded && wasGrounded) sfx.jump();
      if (sim.won) {
        stats[levelIdx] = {
          seconds: sim.levelTicks * TICK,
          echoes: sim.level.def.echoes - sim.echoesLeft,
        };
        rewardLevelClear(levelIdx); // first-clear credits (idempotent per level)
        wonT = 0;
        state = 'won';
        break;
      }
    }
    // button click feedback
    if (sim.pressedButtons.size > prevPressedCount) sfx.button();
    prevPressedCount = sim.pressedButtons.size;
    pumpEvents();

    // echo-freeze feedback — a past self locking into place as a platform is
    // the heart of the game; puff + soft thunk once per echo so the moment reads.
    for (const e of sim.echoes) {
      if (sim.echoFrozen(e) && !frozenEchoIds.has(e.id)) {
        frozenEchoIds.add(e.id);
        const f = sim.echoFrame(e);
        const ex = f.x + PLAYER_W / 2;
        const ey = f.y + PLAYER_H / 2;
        spawnBurst(ex, ey, `hsl(${e.hue}, 95%, 68%)`, 16, 170, 220);
        spawnBurst(ex, ey, '#ffffff', 5, 110, 160);
        flash = Math.max(flash, 0.12);
        sfx.land();
      }
    }

    pushTrail(trail, sim.px, sim.py);
    Platform.tickPlaytime(dt); // active-play credit accrual (daily-capped)
  } else {
    acc = 0;
  }

  ageTrail(trail, dt);
  updateParticles(dt);
  flash = Math.max(0, flash - dt * 1.6);
  redFlash = Math.max(0, redFlash - dt * 1.4);
  rewindSweep = Math.max(0, rewindSweep - dt * 3.2);
  shake = Math.max(0, shake - dt * 22);
  cardT = Math.max(0, cardT - dt);
  lockedFlashT = Math.max(0, lockedFlashT - dt);
  creditPop = Math.max(0, creditPop - dt * 2);

  // camera
  if (state === 'play' || state === 'won') {
    const target = clampCam(sim.px + PLAYER_W / 2 - VIEW_W / 2);
    camX += (target - camX) * Math.min(1, dt * 7);
  }

  render();
  input.endFrame();
}

// ---- render ---------------------------------------------------------------------

function render(): void {
  // The intro is its own self-contained scene (clears + post itself).
  if (state === 'intro' && intro) {
    intro.draw(ctx, time);
    return;
  }

  // The menu + level selector are crisp DOM overlays — nothing on the canvas.
  if (state === 'menu' || state === 'select') return;

  ctx.clearRect(0, 0, VIEW_W, VIEW_H);

  const shaking = shake > 0.2;
  if (shaking) {
    ctx.save();
    ctx.translate((Math.random() - 0.5) * shake * 2, (Math.random() - 0.5) * shake * 2);
  }
  drawBackdrop(ctx, sim.level, camX, time);

  drawPlayerTrail(ctx, trail, camX, sim.level.def.accent);
  drawWorld(ctx, sim, camX, time);
  if (shaking) ctx.restore();
  drawHud();

  if (state === 'won') drawWonOverlay();
  if (state === 'end') drawEndOverlay();
  if (state === 'locked') drawLockedOverlay();

  // flashes
  if (flash > 0) {
    ctx.fillStyle = `rgba(240, 245, 255, ${flash})`;
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  }
  if (redFlash > 0) {
    ctx.fillStyle = `rgba(255, 40, 70, ${redFlash * 0.55})`;
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  }

  // timeline-rewind wipe — a bright edge sweeps right→left when you drop an
  // echo, selling "the run resets to t=0 and your past self replays".
  if (rewindSweep > 0) {
    const x = rewindSweep * (VIEW_W + 160) - 80;
    const a = rewindSweep;
    const sg = ctx.createLinearGradient(x - 80, 0, x + 30, 0);
    sg.addColorStop(0, 'transparent');
    sg.addColorStop(0.7, `rgba(212, 75, 255, ${0.1 * a})`);
    sg.addColorStop(0.9, `rgba(86, 243, 255, ${0.5 * a})`);
    sg.addColorStop(1, `rgba(244, 248, 255, ${0.7 * a})`);
    ctx.fillStyle = sg;
    ctx.fillRect(x - 80, 0, 110, VIEW_H);
  }

  drawPost(ctx);
}

function text(
  str: string,
  x: number,
  y: number,
  size: number,
  color: string,
  align: CanvasTextAlign = 'left',
  weight = '700',
  spacing = 0,
): void {
  ctx.font = `${weight} ${size}px ui-monospace, "SF Mono", Menlo, monospace`;
  ctx.textAlign = align;
  ctx.fillStyle = color;
  if (spacing > 0) {
    // manual letter-spacing
    const chars = str.split('');
    let total = 0;
    for (const c of chars) total += ctx.measureText(c).width + spacing;
    let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
    ctx.textAlign = 'left';
    for (const c of chars) {
      ctx.fillText(c, cx, y);
      cx += ctx.measureText(c).width + spacing;
    }
    ctx.textAlign = align;
  } else {
    ctx.fillText(str, x, y);
  }
}

function drawHud(): void {
  const lv = sim.level.def;
  const s = hudScale; // portrait/phone HUD up-scale (1.0 on desktop)
  // top-left: level tag + echo chips
  text(`${String(levelIdx + 1).padStart(2, '0')} / ${LEVELS.length}`, 18, 28 * s, 12 * s, 'rgba(214,226,240,0.45)', 'left', '600', 2 * s);
  text(lv.name, 18, 48 * s, 17 * s, lv.accent, 'left', '800', 3 * s);

  const chipY = 60 * s;
  const chipW = 14 * s;
  const chipH = 19 * s;
  for (let i = 0; i < lv.echoes; i++) {
    const used = i < lv.echoes - sim.echoesLeft;
    const x = 20 + i * 26 * s;
    ctx.globalAlpha = used ? 0.25 : 0.95;
    ctx.fillStyle = used ? '#6a5e8a' : '#d44bff';
    ctx.beginPath();
    ctx.roundRect(x, chipY, chipW, chipH, 5 * s);
    ctx.fill();
    if (!used) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x + 3.5 * s, chipY + 4 * s, 7 * s, 3 * s);
    }
  }
  ctx.globalAlpha = 1;

  // top-right: REC + attempt clock
  const secs = sim.tape.length * TICK;
  const rec = Math.sin(time * 5) > 0;
  if (rec && !sim.dead) {
    ctx.fillStyle = '#ff4660';
    ctx.beginPath();
    ctx.arc(VIEW_W - 120 * s, 25 * s, 4.5 * s, 0, Math.PI * 2);
    ctx.fill();
  }
  text(`REC ${secs.toFixed(1)}s`, VIEW_W - 18, 30 * s, 13 * s, 'rgba(214,226,240,0.7)', 'right', '600', 1 * s);
  text(`ATTEMPT ${sim.attempt}`, VIEW_W - 18, 50 * s, 11 * s, 'rgba(214,226,240,0.4)', 'right', '500', 2 * s);
  text(
    `${Platform.balance()} CR`,
    VIEW_W - 18, 70 * s, 12 * s,
    creditPop > 0 ? '#ffe08a' : 'rgba(255,217,102,0.75)', 'right', '700', 1 * s,
  );

  // off-screen EXIT pointer — when the goal scrolls out of view, a pulsing edge
  // chevron + label keeps the objective unambiguous (pairs with the portal's
  // own EXIT beacon when it IS on screen).
  const exScreenX = sim.level.exit.col * TILE + TILE / 2 - camX;
  const margin = 30;
  if (exScreenX > VIEW_W - margin || exScreenX < margin) {
    const right = exScreenX > VIEW_W - margin;
    const accent = sim.level.def.accent;
    const ax = right ? VIEW_W - 20 : 20;
    const ay = Math.max(72, Math.min(VIEW_H - 72, sim.level.exit.row * TILE - 6));
    const d = right ? 1 : -1;
    const pulse = 0.55 + 0.45 * Math.sin(time * 4);
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(ax - d * 7, ay - 9);
    ctx.lineTo(ax + d * 7, ay);
    ctx.lineTo(ax - d * 7, ay + 9);
    ctx.stroke();
    ctx.restore();
    text('EXIT', right ? VIEW_W - 14 : 14, ay - 18, 9, accent, right ? 'right' : 'left', '700', 2);
  }

  // first-level tutorial — one clean, prominent prompt at the bottom-centre,
  // shown ONLY on level 1 so later levels stay uncluttered. On phones it sits a
  // little higher so it clears the bottom touch controls.
  if (levelIdx === 0) {
    const ty = VIEW_H - (TOUCH ? 96 : 38);
    const tw = Math.min(VIEW_W - 24, (lv.hint.length * 7.4 + 90) * s);
    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = 'rgba(10,6,19,0.62)';
    ctx.beginPath();
    ctx.roundRect(VIEW_W / 2 - tw / 2, ty - 24 * s, tw, 44 * s, 12);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(86,243,255,0.25)';
    ctx.stroke();
    ctx.restore();
    text('TUTORIAL', VIEW_W / 2, ty - 7 * s, 9 * s, '#56f3ff', 'center', '700', 4 * s);
    text(lv.hint, VIEW_W / 2, ty + 11 * s, 13 * s, '#e7ecf6', 'center', '600', 0.4 * s);
  }

  // level card
  if (cardT > 1.2) {
    const a = Math.min(1, (cardT - 1.2) / 0.6);
    ctx.globalAlpha = a;
    text(lv.name, VIEW_W / 2, VIEW_H / 2 - 60, 38, '#f4f8ff', 'center', '800', 8);
    text(lv.subtitle, VIEW_W / 2, VIEW_H / 2 - 30, 13, lv.accent, 'center', '500', 3);
    ctx.globalAlpha = 1;
  }
}

function drawWonOverlay(): void {
  const a = Math.min(0.75, wonT * 1.5);
  ctx.fillStyle = `rgba(6, 4, 12, ${a})`;
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  const s = stats[levelIdx];
  text('LOOP CLOSED', VIEW_W / 2, 230, 40, sim.level.def.accent, 'center', '800', 8);
  if (s) {
    text(
      `TIME ${s.seconds.toFixed(1)}s   ·   ECHOES USED ${s.echoes}/${sim.level.def.echoes}`,
      VIEW_W / 2, 272, 14, 'rgba(214,226,240,0.8)', 'center', '600', 2,
    );
  }
  if (wonT > 0.7 && Math.sin(time * 3.5) > -0.2) {
    const lastLevel = levelIdx + 1 >= LEVELS.length;
    text(
      lastLevel ? 'PRESS [ENTER] FOR THE VERDICT' : 'PRESS [ENTER] FOR THE NEXT LOOP',
      VIEW_W / 2, 330, 14, '#56f3ff', 'center', '700', 3,
    );
  }
}

function drawEndOverlay(): void {
  ctx.fillStyle = 'rgba(6, 4, 12, 0.88)';
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  text('ALL LOOPS CLOSED', VIEW_W / 2, 56, 38, '#f4f8ff', 'center', '800', 8);

  // Two-column scoreboard so a full ~20-level campaign fits on one screen.
  let total = 0;
  let echoes = 0;
  const half = Math.ceil(LEVELS.length / 2);
  const rowTop = 92;
  const rowGap = 18;
  for (let i = 0; i < LEVELS.length; i++) {
    const lv = LEVELS[i]!;
    const s = stats[i];
    const t = s ? s.seconds : 0;
    const e = s ? s.echoes : 0;
    total += t;
    echoes += e;
    const colLeft = VIEW_W / 2 + (i < half ? -250 : 18);
    const y = rowTop + (i % half) * rowGap;
    text(`${String(i + 1).padStart(2, '0')}  ${lv.name}`, colLeft, y, 12, lv.accent, 'left', '600', 1);
    text(`${t.toFixed(1)}s · ${e}`, colLeft + 232, y, 12, 'rgba(214,226,240,0.65)', 'right', '600', 1);
  }
  const footY = rowTop + half * rowGap + 22;
  text(
    `TOTAL ${total.toFixed(1)}s  ·  ${echoes} PAST LIVES SPENT`,
    VIEW_W / 2, footY, 15, 'rgba(214,226,240,0.9)', 'center', '700', 2,
  );
  if (bestMsg) {
    const isNew = bestMsg.startsWith('NEW');
    text(bestMsg, VIEW_W / 2, footY + 24, 13, isNew ? '#56f3ff' : 'rgba(212,75,255,0.8)', 'center', '700', 3);
  }
  if (Math.sin(time * 3.5) > -0.2) {
    text('PRESS [ENTER] TO LOOP AGAIN', VIEW_W / 2, footY + 54, 14, '#56f3ff', 'center', '700', 3);
  }
}

function drawLockedOverlay(): void {
  const cx = VIEW_W / 2;
  ctx.fillStyle = 'rgba(6, 4, 12, 0.9)';
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);

  const ch = lockedChapter;
  const bal = Platform.balance();
  const price = ch ? ch.price : 0;
  const canBuy = bal >= price;

  text('CHAPTER LOCKED', cx, 132, 34, '#ffd966', 'center', '800', 6);
  if (ch) {
    text(ch.title, cx, 172, 16, '#f4f8ff', 'center', '700', 3);
    text(
      `LEVELS ${ch.from + 1}–${chapterEndLabel(ch)}   ·   UNLOCK FOR ${ch.price} CREDITS`,
      cx, 198, 13, 'rgba(214,226,240,0.7)', 'center', '600', 1,
    );
  }

  text(
    `YOU HAVE ${bal} CREDITS`,
    cx, 244, 15, canBuy ? '#5dff9c' : '#ff6a7a', 'center', '700', 2,
  );

  text(
    canBuy ? '[U]   UNLOCK THIS CHAPTER' : '[U]   unlock  (not enough credits yet)',
    cx, 302, 16, canBuy ? '#56f3ff' : 'rgba(214,226,240,0.4)', 'center', '700', 2,
  );

  if (Platform.adsEnabled()) {
    const adsLeft = Platform.adsRemainingToday();
    text(
      adsLeft > 0
        ? `[V]   WATCH AD   +${Platform.limits.adReward} CREDITS   (${adsLeft} left today)`
        : '[V]   watch ad   (daily limit reached)',
      cx, 334, 14, adsLeft > 0 ? '#b6ff54' : 'rgba(214,226,240,0.4)', 'center', '600', 1,
    );
  }
  text(
    '…or keep PLAYING — every new level you clear pays credits',
    cx, 360, 12, 'rgba(214,226,240,0.6)', 'center', '500', 1,
  );
  text('[ESC]   BACK TO MENU', cx, 396, 12, 'rgba(214,226,240,0.5)', 'center', '600', 2);

  if (lockedFlashT > 0) {
    ctx.globalAlpha = Math.min(1, lockedFlashT);
    text(lockedFlash, cx, 436, 15, '#ffd966', 'center', '700', 2);
    ctx.globalAlpha = 1;
  }
}

// ---- boot -----------------------------------------------------------------------

initInput();
Platform.onChange(() => {
  creditPop = 1;
  // Keep the live selector grid (lock states + credit line) in sync when the
  // wallet or entitlements change (e.g. config loads, a cross-tab purchase).
  if (state === 'select') buildSelectGrid();
});
// Load central tunables (/platform-config.json), THEN grant the config-driven
// welcome bonus. Defaults stand if the file is missing (e.g. local per-game dev).
// loadConfig() also kicks the platform's server session bootstrap internally
// (a no-op while VITE_FEATURE_AUTH is off), so no extra init call is needed.
void Platform.loadConfig().finally(() => {
  Platform.grantWelcomeBonusOnce();
});

// Always-on global credit pill. echoloop runs local-only (no auth client
// injected), so the chip shows just the shared wallet readout; the in-canvas
// credit HUD stays as-is.
mountPlatformChip({ platform: Platform, auth: authClient });
// DEBUG: `?panel` (or ?panel=1) auto-opens the global platform panel so its look
// can be screenshot-verified without a click. Harmless no-op otherwise.
maybeAutoOpenPanel({ platform: Platform, auth: authClient });
window.addEventListener('pointerdown', unlockAudio, { once: false });
// A tap during the cinematic skips it (handled in the frame loop).
window.addEventListener('pointerdown', () => {
  if (state === 'intro') skipIntro = true;
});

// The intro now fires on first entry of level 1 (see enterLevel) — you land on
// the menu first, NOT on a cinematic. `?intro` still force-previews it on load.
const introParams = new URLSearchParams(location.search);
if (introParams.has('intro')) {
  pendingEntry = null;
  startIntro();
}

requestAnimationFrame((t) => {
  last = t;
  requestAnimationFrame(frame);
});
